import { useState } from 'react'
import type { Eip1193Provider } from 'ethers'
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  ExternalLink,
  Trash2,
  Wallet,
  Zap,
} from 'lucide-react'
import { decodeCalldata, short } from '../decode.js'
import { usePrivacy } from '../lib/privacy.js'
import { DecodedCalldata } from './DecodedCalldata.js'
import { ClearSigningPanel } from './ClearSigningPanel.js'
import { useClearSigning } from '../hooks/useClearSigning.js'
import { useAllowances } from '../hooks/useAllowances.js'
import { displayDecimal } from '../erc7730/format.js'
import { SafewardClient } from '../safeward-client.js'
import { CHAIN_ID } from '../lib/constants.js'
import { executeModuleTx } from '../lib/execTx.js'
import { deleteModuleTx } from '../lib/deleteTx.js'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import type {
  ApprovalFlow,
  AssetFlow,
  DecideResponse,
  Hex,
  InternalRevert,
  NetBalanceChange,
  PendingTx,
  SimEvent,
  SimulationFlows,
} from '../types.js'

/** The call the Safe actually makes (inner call for a module tx), plus the
 *  Safe whose allowances that call can spend. */
interface CallTarget {
  owner: string
  spender: string
  data: string
  name: string | null
}

interface Props {
  tx: PendingTx
  decision?: DecideResponse
  decisionError?: string
  guardianAddress: string | null
  moduleSafe: string
  safewardUrl: string
  safewardAuthToken: string
  isLatest: boolean
  walletProvider: Eip1193Provider | undefined
  signerAddress: string | undefined
  onAfterSign: () => void
}

export function TxCard({
  tx,
  decision,
  decisionError,
  guardianAddress,
  moduleSafe,
  safewardUrl,
  safewardAuthToken,
  isLatest,
  walletProvider,
  signerAddress,
  onAfterSign,
}: Props) {
  const [busy, setBusy] = useState(false)
  const [signMsg, setSignMsg] = useState<string | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [deleteMsg, setDeleteMsg] = useState<string | null>(null)
  const [execBusy, setExecBusy] = useState(false)
  const [execMsg, setExecMsg] = useState<string | null>(null)
  const [execTxHash, setExecTxHash] = useState<string | null>(null)
  const signerConnected = !!walletProvider && !!signerAddress
  const privacy = usePrivacy()

  const decoded = decodeCalldata(tx.to, tx.value, tx.data)
  const callTo = decoded.inner?.to ?? tx.to
  const callData = decoded.inner?.data ?? tx.data
  const callValue = decoded.inner?.value ?? tx.value
  const clearSigning = useClearSigning(callTo, callData, callValue)
  const target: CallTarget = {
    // execTransactionFromModule runs the inner call as the protected Safe (tx.to).
    owner: decoded.inner ? tx.to : tx.safe,
    spender: callTo,
    data: callData,
    name: clearSigning?.contractName ?? null,
  }
  const thresholdMet = tx.confirmations.length >= tx.confirmationsRequired
  const guardianSigned =
    guardianAddress !== null &&
    tx.confirmations.some((c) => c.owner.toLowerCase() === guardianAddress)

  const sigBadgeText = thresholdMet
    ? `${tx.confirmations.length}/${tx.confirmationsRequired} · ready to execute`
    : `${tx.confirmations.length}/${tx.confirmationsRequired} sigs`
  const sigBadgeClass = thresholdMet
    ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
    : 'border-amber-500/30 bg-amber-500/10 text-amber-400'

  const onSign = async () => {
    setBusy(true)
    setSignMsg('signing…')
    try {
      const client = new SafewardClient({
        baseUrl: safewardUrl,
        authToken: safewardAuthToken,
      })
      const r = await client.signPending(tx.safeTxHash, moduleSafe || undefined)
      if (r.signed && r.posted) {
        setSignMsg('✅ posted confirmation')
        setTimeout(onAfterSign, 750)
      } else {
        setSignMsg(`❌ refused: ${r.decision.reason}`)
        setBusy(false)
      }
    } catch (err) {
      setSignMsg(`❌ ${(err as Error).message}`)
      setBusy(false)
    }
  }

  const onDelete = async () => {
    if (!walletProvider || !signerAddress) return
    if (
      !window.confirm(
        'Delete this transaction from the queue?\n\nAlready-given signatures stay valid, but the tx is permanently removed and the next nonce reopens for proposal.',
      )
    ) {
      return
    }
    setDeleteBusy(true)
    setDeleteMsg('signing…')
    try {
      await deleteModuleTx({
        safeTxHash: tx.safeTxHash as Hex,
        walletProvider,
        signerAddress,
        moduleSafe,
        chainId: CHAIN_ID,
        onProgress: (stage) =>
          setDeleteMsg(stage === 'signing' ? 'signing…' : 'deleting…'),
      })
      setDeleteMsg('✅ deleted')
      setTimeout(onAfterSign, 750)
    } catch (err) {
      setDeleteMsg(`❌ ${(err as Error).message}`)
      setDeleteBusy(false)
    }
  }

  const deleteTitle = execBusy
    ? 'Cannot delete while an execTransaction is in flight.'
    : !isLatest
      ? 'Only the latest queued transaction (highest nonce) can be deleted — removing an earlier one would leave a nonce gap that blocks every later tx.'
      : !signerConnected
        ? 'Pair a signer wallet first — Safe Tx Service requires an owner signature to delete.'
        : 'Permanently remove this tx from the queue. Already-given signatures stay valid.'

  const onExecute = async () => {
    if (!walletProvider || !signerAddress) return
    setExecBusy(true)
    setExecTxHash(null)
    setExecMsg('re-simulating…')
    try {
      const result = await executeModuleTx({
        tx,
        walletProvider,
        signerAddress,
        moduleSafe,
        safewardUrl,
        safewardAuthToken,
        onProgress: (stage) => {
          switch (stage.kind) {
            case 'simulating':
              setExecMsg('re-simulating…')
              return
            case 'simulated':
              setExecMsg(
                stage.approved
                  ? '✓ sim approved — confirm in wallet…'
                  : `⚠ sim rejects (${stage.reason}) — confirm in wallet…`,
              )
              return
            case 'sim-error':
              setExecMsg(`⚠ sim unavailable (${stage.message}) — confirm in wallet…`)
              return
            case 'awaiting-wallet':
              return
            case 'broadcast':
              setExecTxHash(stage.txHash)
              return
            case 'waiting-receipt':
              setExecMsg('broadcast — waiting for receipt…')
              return
          }
        },
      })
      if (result.kind === 'success') {
        setExecMsg('✅ executed')
        setTimeout(onAfterSign, 4000)
      } else if (result.kind === 'reverted') {
        setExecMsg('❌ tx reverted on-chain')
        setExecBusy(false)
      } else {
        const which =
          result.failedAt === 'main'
            ? 'Inner module call reverted (Main Safe → target)'
            : 'Module Safe execTransaction reported failure'
        setExecMsg(`❌ ${which} — see Etherscan trace`)
        setExecBusy(false)
      }
    } catch (err) {
      setExecMsg(`❌ ${(err as Error).message}`)
      setExecBusy(false)
    }
  }

  const primaryButton = (() => {
    if (thresholdMet && !tx.isExecuted) {
      return (
        <Button
          onClick={() => void onExecute()}
          disabled={execBusy || !signerConnected}
          title={
            !signerConnected
              ? 'Pair a signer wallet first — execTransaction needs an EOA to broadcast and pay gas.'
              : 'Submit execTransaction on-chain. You pay gas; the Module Safe runs the call.'
          }
        >
          <Zap />
          {execBusy ? 'Executing…' : 'Execute on-chain'}
        </Button>
      )
    }
    if (guardianSigned) {
      return (
        <Button disabled variant="secondary">
          ✓ Guardian signed
        </Button>
      )
    }
    if (decision && !decision.decision.approved) {
      return (
        <Button disabled variant="destructive">
          Rejected by policy
        </Button>
      )
    }
    return (
      <Button
        onClick={() => void onSign()}
        disabled={busy || !decision || !decision.decision.approved}
      >
        Sign as Guardian
      </Button>
    )
  })()

  return (
    <Card className="mb-4">
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="font-mono text-base">
          #{tx.nonce} · {short(tx.safeTxHash)}
        </CardTitle>
        <Badge className={sigBadgeClass}>{sigBadgeText}</Badge>
      </CardHeader>
      <CardContent className="space-y-4">
        <KvList>
          <Kv label="Summary">
            <span className="font-mono" data-mask>
              {decoded.summary}
            </span>
          </Kv>
          {decoded.inner ? (
            <>
              <Kv label="Inner target">
                <span className="font-mono">{short(decoded.inner.to)}</span>
              </Kv>
              <Kv label="Inner calldata">
                <code className="tx-data block max-h-20 overflow-auto rounded border bg-background px-2 py-1.5 font-mono text-[11px] break-all">
                  {decoded.inner.data}
                </code>
              </Kv>
            </>
          ) : (
            <>
              <Kv label="Target">
                <span className="font-mono">{short(tx.to)}</span>
              </Kv>
              <Kv label="Calldata">
                <code className="tx-data block max-h-20 overflow-auto rounded border bg-background px-2 py-1.5 font-mono text-[11px] break-all">
                  {tx.data}
                </code>
              </Kv>
            </>
          )}
        </KvList>

        <ClearSigningPanel to={callTo} data={callData} value={callValue} />
        <DecodedCalldata data={callData} collapsed={clearSigning !== null} />

        <KvList>
          <Kv label="Value">
            <span className="font-mono" data-mask>
              {tx.value} wei
            </span>
          </Kv>
          <Kv label="Proposer">
            <span className="font-mono">
              {tx.proposer ? short(tx.proposer) : '—'}
            </span>
          </Kv>
          <Kv label="Signed by">
            <ConfirmationsList
              confirmations={tx.confirmations}
              guardianAddress={guardianAddress}
            />
          </Kv>
        </KvList>

        <Separator />

        {decisionError ? (
          <div className="text-sm text-destructive">
            simulation failed: {decisionError}
          </div>
        ) : decision ? (
          <Decision res={decision} privacy={privacy} target={target} />
        ) : (
          <div className="text-sm text-muted-foreground">Simulating…</div>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-2">
        {primaryButton}
        {signMsg && (
          <span className="text-xs text-muted-foreground">{signMsg}</span>
        )}
        {execMsg && (
          <span className="text-xs text-muted-foreground">{execMsg}</span>
        )}
        {execTxHash && (
          <a
            href={`https://etherscan.io/tx/${execTxHash}`}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1 font-mono text-xs text-sky-400 hover:underline"
            title={execTxHash}
          >
            <ExternalLink className="size-3" />
            {short(execTxHash)} on Etherscan
          </a>
        )}
        <Button
          variant="destructive"
          onClick={() => void onDelete()}
          disabled={!isLatest || !signerConnected || deleteBusy || execBusy}
          title={deleteTitle}
          className="ml-auto"
        >
          <Trash2 />
          {deleteBusy ? 'Deleting…' : 'Delete from queue'}
        </Button>
        {deleteMsg && (
          <span className="text-xs text-muted-foreground">{deleteMsg}</span>
        )}
      </CardFooter>
    </Card>
  )
}

function KvList({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-2 text-sm">
      {children}
    </dl>
  )
}

function Kv({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 break-all">{children}</dd>
    </>
  )
}

function ConfirmationsList({
  confirmations,
  guardianAddress,
}: {
  confirmations: { owner: string; signature: string }[]
  guardianAddress: string | null
}) {
  if (confirmations.length === 0)
    return <span className="text-muted-foreground">nobody yet</span>
  return (
    <div className="space-y-1">
      {confirmations.map((c) => {
        const isGuardian =
          guardianAddress !== null && c.owner.toLowerCase() === guardianAddress
        return (
          <div key={c.owner} className="font-mono text-xs">
            ✓{' '}
            {isGuardian ? (
              <span className="font-semibold text-emerald-400">guardian</span>
            ) : (
              <span className="text-muted-foreground">owner</span>
            )}{' '}
            <span className="text-muted-foreground">{short(c.owner)}</span>
          </div>
        )
      })}
    </div>
  )
}

function Decision({
  res,
  privacy,
  target,
}: {
  res: DecideResponse
  privacy: boolean
  target: CallTarget
}) {
  const { decision, simulation, safeTxHash } = res
  const verdictCls = decision.approved ? 'text-emerald-400' : 'text-destructive'
  const verdict = decision.approved ? '✅ approved' : '❌ rejected'
  const usd = fmtUsdDelta(decision.usdDelta)

  return (
    <div className="space-y-2">
      <div className={cn('font-semibold', verdictCls)}>
        {verdict} · net {usd}
      </div>
      <div className="text-xs text-muted-foreground">{decision.reason}</div>
      {simulation && <Flows sim={simulation} target={target} />}
      <details className="text-xs">
        <summary className="cursor-pointer text-muted-foreground">
          policy checks
        </summary>
        <div className="mt-2 space-y-1.5">
          {Object.entries(decision.checks).map(([name, c]) => (
            <div
              key={name}
              className={cn(
                'grid grid-cols-[16px_160px_1fr] items-baseline gap-2 text-xs',
                c.passed ? 'text-emerald-400' : 'text-destructive',
              )}
            >
              <span>{c.passed ? '✓' : '✗'}</span>
              <span>{name}</span>
              <span>{c.detail}</span>
            </div>
          ))}
        </div>
      </details>
      <div className="text-xs text-muted-foreground">
        safeTxHash:{' '}
        <span className="font-mono">
          {privacy ? short(safeTxHash) : safeTxHash}
        </span>
      </div>
    </div>
  )
}

function Flows({ sim, target }: { sim: SimulationFlows; target: CallTarget }) {
  const hasFlows =
    sim.flows.sent.length > 0 ||
    sim.flows.received.length > 0 ||
    sim.flows.intermediate.length > 0
  const hasApprovals =
    sim.approvals.fromSafe.length > 0 || sim.approvals.other.length > 0
  const hasInnerReverts = sim.internalReverts.length > 0

  if (hasInnerReverts)
    return <InnerReverts reverts={sim.internalReverts} url={sim.url} />

  if (!hasFlows && !hasApprovals) {
    return (
      <div className="space-y-2">
        {sim.url && <TenderlyLink url={sim.url} />}
        <NoMovements events={sim.events ?? []} target={target} />
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {sim.url && <TenderlyLink url={sim.url} />}
      {sim.netByAddress.length > 0 && <NetBalances net={sim.netByAddress} />}
      <details>
        <summary className="cursor-pointer text-xs text-muted-foreground">
          token movements
        </summary>
        <div className="mt-2 space-y-2">
          {sim.flows.sent.length > 0 && (
            <FlowBlock
              label="Safe sent"
              icon="up"
              flows={sim.flows.sent}
              tone="bad"
            />
          )}
          {sim.flows.received.length > 0 && (
            <FlowBlock
              label="Safe received"
              icon="down"
              flows={sim.flows.received}
              tone="good"
            />
          )}
          {sim.flows.intermediate.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer text-xs text-muted-foreground">
                {sim.flows.intermediate.length} intermediate hop
                {sim.flows.intermediate.length === 1 ? '' : 's'}
              </summary>
              <FlowBlock
                label=""
                icon={null}
                flows={sim.flows.intermediate}
                tone="muted"
              />
            </details>
          )}
        </div>
      </details>
      {sim.approvals.fromSafe.length > 0 && (
        <Approvals approvals={sim.approvals.fromSafe} />
      )}
    </div>
  )
}

const UNLIMITED = 2n ** 255n

/**
 * Empty-simulation panel. Policy passes a tx that moves nothing, so this says
 * what can still move: a keeper filling an order later spends the Safe's
 * standing allowance to the target, a transfer this simulation never sees.
 */
function NoMovements({ events, target }: { events: SimEvent[]; target: CallTarget }) {
  const allowances = useAllowances(target.owner, target.spender, target.data, true)
  const name = target.name ?? short(target.spender)
  const named = events.filter((e) => e.name).map((e) => e.name!)
  const unnamed = events.length - named.length
  const pulls =
    allowances.status === 'ok' ? allowances.allowances.filter((a) => a.amount > 0n) : []
  const blocked =
    allowances.status === 'ok' ? allowances.allowances.filter((a) => a.amount === 0n) : []
  const explained = events.length > 0 || allowances.status !== 'idle'

  return (
    <div className="space-y-1.5 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-amber-400">
        <AlertTriangle className="size-4" />
        Nothing moved in this transaction
      </div>
      {pulls.length > 0 && (
        <div>
          {name} can pull from the Safe later:{' '}
          {pulls.map((a, i) => (
            <span key={a.token} className="font-mono font-semibold" data-mask>
              {i > 0 && ', '}
              {a.amount >= UNLIMITED ? 'unlimited' : displayDecimal(a.amount, a.decimals)}{' '}
              {a.symbol}
            </span>
          ))}
          <span className="text-muted-foreground"> (existing allowance)</span>
        </div>
      )}
      {blocked.length > 0 && (
        <div>
          No {blocked.map((a) => a.symbol).join(', ')} allowance to {name}, so it
          can't pull funds yet.
        </div>
      )}
      {allowances.status === 'loading' && (
        <div className="text-muted-foreground">Checking allowances…</div>
      )}
      {allowances.status === 'error' && (
        <div className="text-destructive">Couldn't read allowances: {allowances.error}</div>
      )}
      {events.length > 0 && (
        <div className="text-muted-foreground">
          Emitted:{' '}
          <span className="font-mono">
            {[...named, ...(unnamed > 0 ? [`${unnamed} unnamed log${unnamed === 1 ? '' : 's'}`] : [])].join(', ')}
          </span>
        </div>
      )}
      {!explained && (
        <div className="text-muted-foreground">
          Possibly a replay or no-op. Executing can still revert.
        </div>
      )}
    </div>
  )
}

function TenderlyLink({ url }: { url: string }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener"
      className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline"
    >
      <ExternalLink className="size-3" />
      View simulation trace on Tenderly
    </a>
  )
}

function InnerReverts({
  reverts,
  url,
}: {
  reverts: InternalRevert[]
  url: string | null
}) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
        <AlertTriangle className="size-4" />
        Internal reverts detected ({reverts.length})
      </div>
      <div className="mt-1 text-xs text-muted-foreground">
        One or more sub-calls reverted even though the top-level tx reported
        success.
      </div>
      <div className="mt-2 space-y-1">
        {reverts.map((r, i) => (
          <div
            key={i}
            className="grid grid-cols-[140px_1fr] items-baseline gap-2 rounded-sm bg-destructive/10 px-2 py-1.5 text-xs"
          >
            <span className="font-mono text-muted-foreground">
              {short(r.address)}
            </span>
            <span className="font-medium text-destructive">
              {r.reason ?? r.error}
            </span>
          </div>
        ))}
      </div>
      {url && (
        <div className="mt-2">
          <a
            href={url}
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1 text-xs text-sky-400 hover:underline"
          >
            <ExternalLink className="size-3" />
            Drill into the trace on Tenderly
          </a>
        </div>
      )}
    </div>
  )
}

function NetBalances({ net }: { net: NetBalanceChange[] }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Wallet className="size-3" />
        Net balance change after execution
      </div>
      <div className="mt-1 space-y-1">
        {net.map((n) => {
          const usd =
            (n.netUsd >= 0 ? '+$' : '-$') +
            Math.abs(n.netUsd).toFixed(Math.abs(n.netUsd) < 1 ? 4 : 2)
          const dangerous = n.label === 'external' && n.netUsd > 0
          const labelText =
            n.label === 'protected'
              ? 'SAFE'
              : n.label === 'module'
                ? 'MODULE'
                : n.label === 'whitelisted'
                  ? 'PROTOCOL'
                  : 'EXTERNAL'
          const labelCls = dangerous
            ? 'text-destructive'
            : n.label === 'protected' || n.label === 'module'
              ? 'text-emerald-400'
              : 'text-muted-foreground'
          const usdCls = n.netUsd >= 0 ? 'text-emerald-400' : 'text-destructive'
          return (
            <div
              key={n.address}
              className={cn(
                'grid grid-cols-[100px_120px_1fr_auto] items-baseline gap-2 rounded-sm bg-muted/30 px-2 py-1.5 text-xs',
                dangerous && 'border-l-2 border-destructive pl-2',
              )}
            >
              <span
                className={cn(
                  'text-[10px] font-semibold uppercase tracking-wide',
                  labelCls,
                )}
              >
                {labelText}
              </span>
              <span
                className={cn('text-right font-mono font-semibold', usdCls)}
              >
                {usd}
              </span>
              <span className="font-mono text-muted-foreground">
                {short(n.address)}
              </span>
              {dangerous ? (
                <span className="text-[10px] font-semibold text-destructive">
                  ⚠ EXITS SAFE
                </span>
              ) : (
                <span></span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

const TONE_CLS: Record<'good' | 'bad' | 'muted', string> = {
  good: 'text-emerald-400',
  bad: 'text-destructive',
  muted: 'text-muted-foreground',
}

function FlowBlock({
  label,
  icon,
  flows,
  tone,
}: {
  label: string
  icon: 'up' | 'down' | null
  flows: AssetFlow[]
  tone: 'good' | 'bad' | 'muted'
}) {
  return (
    <div>
      {label && (
        <div
          className={cn(
            'flex items-center gap-1.5 text-xs',
            TONE_CLS[tone],
          )}
        >
          {icon === 'up' && <ArrowUp className="size-3" />}
          {icon === 'down' && <ArrowDown className="size-3" />}
          {label}
        </div>
      )}
      <div className="mt-1 space-y-1">
        {flows.map((f, i) => (
          <div
            key={i}
            className="grid grid-cols-[80px_120px_70px_1fr] items-baseline gap-2 rounded-sm bg-muted/30 px-2 py-1.5 text-xs"
          >
            <span
              className={cn(
                'text-[10px] font-semibold uppercase tracking-wide',
                TONE_CLS[tone],
              )}
            >
              {f.symbol}
            </span>
            <span className="text-right font-mono">
              {formatTokenAmount(f.amount, f.decimals)}
            </span>
            <span className="text-right text-muted-foreground">
              {f.dollarValue !== null ? `$${f.dollarValue.toFixed(2)}` : '—'}
            </span>
            <span className="font-mono text-muted-foreground">
              → {short(f.to)}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function Approvals({ approvals }: { approvals: ApprovalFlow[] }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <AlertTriangle className="size-3 text-amber-400" />
        approvals from Safe
      </div>
      <div className="mt-1 space-y-1">
        {approvals.map((a, i) => {
          const max = BigInt(a.value) === 2n ** 256n - 1n
          const valueLabel = max ? 'UNLIMITED' : formatTokenAmount(a.value, 18)
          const dangerous = max || BigInt(a.value) > 0n
          return (
            <div
              key={i}
              className="grid grid-cols-[80px_120px_1fr_1fr] items-baseline gap-2 rounded-sm bg-muted/30 px-2 py-1.5 text-xs"
            >
              <span
                className={cn(
                  'text-[10px] font-semibold uppercase tracking-wide',
                  dangerous ? 'text-destructive' : 'text-muted-foreground',
                )}
              >
                approve
              </span>
              <span className="font-mono">{valueLabel}</span>
              <span className="font-mono text-muted-foreground">
                token {short(a.tokenAddress)}
              </span>
              <span className="font-mono text-muted-foreground">
                → {short(a.spender)}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function formatTokenAmount(raw: string, decimals: number): string {
  try {
    const big = BigInt(raw)
    if (big === 0n) return '0'
    const divisor = 10n ** BigInt(decimals)
    const whole = big / divisor
    const frac = big % divisor
    if (frac === 0n) return whole.toString()
    const fracStr = frac.toString().padStart(decimals, '0').replace(/0+$/, '')
    return `${whole}.${fracStr.slice(0, 6)}`
  } catch {
    return raw
  }
}

function fmtUsdDelta(n: number): string {
  const sign = n >= 0 ? '+$' : '-$'
  const abs = Math.abs(n)
  return sign + abs.toFixed(2)
}
